import React from "react";
import { Icon } from "@iconify/react";
import Button from "../../components/Button";

// Import only types at runtime from the page that exports them to avoid runtime circular imports.
import type { LoanApiResponse } from "../../../src/pages/loans/LoanDues";
import { useAuth } from "../../context/AuthContext";

export interface LoanTopCardsProps {
  loan: LoanApiResponse;
  isAdmin: boolean;
  onViewVehicle?: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  // formatting helpers are passed in to avoid runtime circular imports
  fmtCurrency: (val?: string | number | null) => string;
  fmtDate: (iso?: string | null) => string;
}

export default function LoanTopCards({
  loan,
  isAdmin,
  onViewVehicle,
  onDelete,
  onEdit,
  fmtCurrency,
  fmtDate,
}: LoanTopCardsProps) {
  const { user } = useAuth();
  const isCustomer = user?.role === "Customer";
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="col-span-1 bg-white rounded-lg shadow-sm p-6 flex flex-col gap-4 items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-semibold">
          {loan.borrower?.first_name?.[0] ?? "U"}
          {loan.borrower?.last_name?.[0] ?? ""}
        </div>
        <div className="flex-1 text-center">
          <h3 className="text-sm font-semibold">
            {loan.borrower?.first_name ?? "Unknown"}{" "}
            {loan.borrower?.last_name ?? ""}
          </h3>
          <div>
            <p className="text-xs text-gray-500">
              {loan.borrower?.customer_code ?? ""}
            </p>
            <p className="text-xs text-gray-500 mb-3">
              <span>Loan Code: </span> {loan.loan_code ?? ""}
            </p>
          </div>
          <hr className="text-gray-200 w-70" />
          <p className="text-sm text-gray-500 mt-2">Vehicle Registration</p>
          <p className="text-xl text-blue-500 mt-1 font-bold ">
            {loan.vehicle?.registration_number ?? ""}
          </p>
          <Button variant="blue" className="mt-2" onClick={onViewVehicle}>
            View Vehicle Details
            <Icon icon="tabler:arrow-up-right" width={20}></Icon>
          </Button>
        </div>
      </div>

      <div className="md:col-span-2 bg-white rounded-lg shadow-sm p-6">
        <div className="grid grid-cols-2 ">
          <h3 className="text-lg font-semibold mb-4">Loan Summary</h3>
          {!isCustomer && (
            <div className="text-end flex justify-end">
              <button
                onClick={onEdit}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
                title="Edit loan details"
              >
                <Icon icon="akar-icons:edit" width={20}></Icon>
              </button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm text-gray-700">
          <div>
            <div className="text-xs text-gray-400">Principal Amount:</div>
            <div className="font-semibold">{fmtCurrency(loan.loan_amount)}</div>

            <div className="mt-4 text-xs text-gray-400">Start Date:</div>
            <div className="font-semibold">
              {fmtDate(loan.start_date ?? loan.disbursement_date)}
            </div>
          </div>

          <div>
            <div className="text-xs text-gray-400">Interest Rate:</div>
            <div className="font-semibold">
              {loan.interest_rate ?? "—"}% p.a.
            </div>

            <div className="mt-4 text-xs text-gray-400">EMI Amount:</div>
            <div className="font-semibold text-green-600">
              {fmtCurrency(loan.emi_amount)}
            </div>
          </div>

          <div>
            <div className="text-xs text-gray-400">Tenure:</div>
            <div className="font-semibold">
              {loan.tenure_months ?? "—"} Months
            </div>

            <div className="mt-4 text-xs text-gray-400">Total Due:</div>
            <div className="font-semibold text-red-600">
              {fmtCurrency(loan.total_amount)}
            </div>
          </div>

          <div>
            <div className="text-xs text-gray-400">Created by ID:</div>
            <div className="font-semibold">{loan?.created_by_id ?? ""}</div>
          </div>
          <div>
            <div className="text-xs text-gray-400">Created by:</div>
            <div className="font-semibold">{loan?.created_by_role ?? ""}</div>
          </div>
          <div>
            <div className="text-xs text-gray-400">Created Date:</div>
            <div className="font-semibold">
              {fmtDate(loan?.loan_created_date ?? "")}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400">End Date:</div>
            <div className="font-semibold">{fmtDate(loan?.end_date ?? "")}</div>
          </div>

          {/* {isAdmin && onDelete && (
            <div className="sm:col-span-3 flex justify-end">
              <Button variant="danger" onClick={onDelete}>
                Delete
              </Button>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
}
